import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Calendar, MapPin, ExternalLink } from 'lucide-react';
import { getAdminAppearances, createAppearance, updateAppearance, deleteAppearance, uploadFile, getArtists, getImageUrl } from '../../utils/api';
import { toast } from 'sonner';
import './AdminStyles.css';

const AppearanceManagement = () => {
  const [appearances, setAppearances] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAppearance, setEditingAppearance] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    artist_id: '',
    artist_name: '',
    venue: '',
    city: '',
    date: '',
    time: '',
    image: '',
    description: '',
    external_link: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appearancesData, artistsData] = await Promise.all([
        getAdminAppearances(),
        getArtists()
      ]);
      setAppearances(appearancesData);
      setArtists(artistsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to load data. Please refresh the page.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (appearance) => {
    setEditingAppearance(appearance);
    setFormData({
      title: appearance.title,
      artist_id: appearance.artist_id,
      artist_name: appearance.artist_name,
      venue: appearance.venue,
      city: appearance.city,
      date: appearance.date,
      time: appearance.time || '',
      image: appearance.image || '',
      description: appearance.description || '',
      external_link: appearance.external_link || ''
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingAppearance(null);
    setFormData({
      title: '',
      artist_id: '',
      artist_name: '',
      venue: '',
      city: '',
      date: '',
      time: '',
      image: '',
      description: '',
      external_link: ''
    });
    setShowModal(true);
  };

  const handleArtistChange = (e) => {
    const artistId = e.target.value;
    const artist = artists.find(a => a.id === artistId);
    setFormData({
      ...formData,
      artist_id: artistId,
      artist_name: artist ? artist.name : ''
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const result = await uploadFile(file);
      setFormData({ ...formData, image: result.url });
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to upload image. Please try again.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingAppearance) {
        await updateAppearance(editingAppearance.id, formData);
        toast.success('Appearance updated');
      } else {
        await createAppearance(formData);
        toast.success('Appearance created');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save appearance');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this appearance?')) return;
    
    try {
      await deleteAppearance(id);
      toast.success('Appearance deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete appearance');
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-section">
      <div className="admin-header">
        <h2>Artist Appearances</h2>
        <p>Manage external events where AMR artists are performing</p>
        <button className="btn btn-primary" onClick={handleAdd}>
          <Plus size={18} /> Add Appearance
        </button>
      </div>

      <div className="admin-table-container">
        {appearances.length === 0 && (
          <div style={{padding: 'var(--spacing-xl)', textAlign: 'center', color: 'var(--light-grey)'}}>
            No appearances yet. Click "Add Appearance" to create one.
          </div>
        )}
        {appearances.length > 0 && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Artist</th>
                <th>Venue</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {appearances.map((appearance) => {
                return (
                  <tr key={appearance.id}>
                    <td>
                      <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        {appearance.image && (
                          <img src={getImageUrl(appearance.image)} alt="" style={{width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px'}} />
                        )}
                        <span>{appearance.title}</span>
                      </div>
                    </td>
                    <td>{appearance.artist_name}</td>
                    <td>{appearance.venue}, {appearance.city}</td>
                    <td>{new Date(appearance.date).toLocaleDateString()}</td>
                    <td>
                      <div className="table-actions">
                        {appearance.external_link && (
                          <a href={appearance.external_link} target="_blank" rel="noopener noreferrer" className="action-btn">
                            <ExternalLink size={16} />
                          </a>
                        )}
                        <button className="action-btn" onClick={() => handleEdit(appearance)}>
                          <Edit size={16} /> Edit
                        </button>
                        <button className="action-btn delete" onClick={() => handleDelete(appearance.id)}>
                          <Trash2 size={16} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingAppearance ? 'Edit Appearance' : 'Add Appearance'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)} type="button">×</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Event Name *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Ultra Music Festival"
                  required
                />
              </div>

              <div className="form-group">
                <label>Artist *</label>
                <select
                  value={formData.artist_id}
                  onChange={handleArtistChange}
                  required
                >
                  <option value="">Select Artist</option>
                  {artists.map(artist => (
                    <option key={artist.id} value={artist.id}>{artist.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Venue *</label>
                  <input
                    type="text"
                    value={formData.venue}
                    onChange={(e) => setFormData({...formData, venue: e.target.value})}
                    placeholder="Venue name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder="City"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Time</label>
                  <input
                    type="text"
                    value={formData.time}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                    placeholder="e.g., 22:00"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>External Link</label>
                <input
                  type="url"
                  value={formData.external_link}
                  onChange={(e) => setFormData({...formData, external_link: e.target.value})}
                  placeholder="https://event-website.com"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of the event..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Event Image</label>
                <span className="dimension-hint">Recommended: 800 x 600px (landscape)</span>
                <div className="file-upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    id="appearance-image-upload"
                    style={{display: 'none'}}
                  />
                  <label htmlFor="appearance-image-upload" className="upload-btn">
                    Upload Image
                  </label>
                  {formData.image && (
                    <div className="image-preview">
                      <img src={getImageUrl(formData.image)} alt="Preview" />
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingAppearance ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppearanceManagement;
